import { Injectable, signal, effect, computed, OnDestroy } from '@angular/core';

/**
 * Theme preferences for the application
 * - system: Follow OS preference
 * - light: Force light mode
 * - dark: Force dark mode
 */
export type ThemePreference = 'system' | 'light' | 'dark';

/**
 * Service to manage application theme (dark mode)
 * Supports system preference detection and manual override
 * Persists preference to localStorage
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService implements OnDestroy {
  /** LocalStorage key for theme preference */
  private readonly STORAGE_KEY = 'theme-preference';

  /**
   * Current theme preference signal
   * Default to system preference
   */
  private readonly _themePreference = signal<ThemePreference>(this._loadThemePreference());

  /**
   * Public readonly signal for theme preference
   */
  readonly themePreference = this._themePreference.asReadonly();

  /**
   * Computed signal indicating whether dark mode is currently active
   * Takes into account both manual preference and system preference
   */
  readonly isDarkMode = computed(() => {
    const preference = this._themePreference();
    return this._shouldUseDarkMode(preference);
  });

  /**
   * Computed signal indicating whether user is using custom theme (not system)
   */
  readonly isCustomTheme = computed(() => this._themePreference() !== 'system');

  /**
   * Media query to detect system dark mode preference
   */
  private readonly _systemDarkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');

  /**
   * Handler for system preference changes
   * Stored as property to allow proper cleanup in ngOnDestroy
   */
  private readonly _handleSystemChange = (): void => {
    // Only react if user is using system preference
    if (this._themePreference() === 'system') {
      this._applyTheme();
    }
  };

  constructor() {
    // Apply theme on initialization
    this._applyTheme();

    // Watch for system preference changes
    this._systemDarkModeQuery.addEventListener('change', this._handleSystemChange);

    // Effect to apply theme whenever preference changes
    effect(() => {
      const preference = this._themePreference();
      this._applyTheme();
      this._saveThemePreference(preference);
    });
  }

  /**
   * Cleanup resources when service is destroyed
   * Removes event listener to prevent memory leaks
   */
  ngOnDestroy(): void {
    this._systemDarkModeQuery.removeEventListener('change', this._handleSystemChange);
  }

  /**
   * Set theme preference
   * @param preference - Theme preference to set
   */
  setThemePreference(preference: ThemePreference): void {
    this._themePreference.set(preference);
  }

  /**
   * Toggle between system and custom theme mode
   * When switching to custom, defaults to current effective theme
   * @param useCustom - True to use custom theme, false to use system
   */
  setCustomTheme(useCustom: boolean): void {
    if (useCustom) {
      // When switching to custom, use current effective theme
      this._themePreference.set(this.isDarkMode() ? 'dark' : 'light');
    } else {
      this._themePreference.set('system');
    }
  }

  /**
   * Set dark mode (only effective when using custom theme)
   * @param useDark - True to use dark mode, false for light mode
   */
  setDarkMode(useDark: boolean): void {
    if (this.isCustomTheme()) {
      this._themePreference.set(useDark ? 'dark' : 'light');
    }
  }

  /**
   * Apply the current theme to the document element
   */
  private _applyTheme(): void {
    const preference = this._themePreference();
    const shouldBeDark = this._shouldUseDarkMode(preference);

    if (shouldBeDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  /**
   * Determine if dark mode should be active based on preference
   * @param preference - Theme preference
   * @returns True if dark mode should be active
   */
  private _shouldUseDarkMode(preference: ThemePreference): boolean {
    switch (preference) {
      case 'dark':
        return true;
      case 'light':
        return false;
      case 'system':
        return this._systemDarkModeQuery.matches;
      default:
        return false;
    }
  }

  /**
   * Load theme preference from localStorage
   * Defaults to 'system' if not set
   */
  private _loadThemePreference(): ThemePreference {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored === 'system' || stored === 'light' || stored === 'dark') {
        return stored;
      }
    } catch {
      // localStorage not available (SSR, private mode, etc.)
    }
    return 'system';
  }

  /**
   * Save theme preference to localStorage
   * @param preference - Theme preference to save
   */
  private _saveThemePreference(preference: ThemePreference): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, preference);
    } catch {
      // localStorage not available (SSR, private mode, etc.)
    }
  }
}
